import { DMY_CASUAL_FORMAT, SHORT_MONTH_LONG_YEAR } from 'constants/dateFormats'
import moment from 'moment'
import type { IntlShape } from 'react-intl'

export const REQUEST_FLIGHT_SUPPORT_CALLBACK_PATH = 'flightsupportcallback'
export const REQUEST_SUPPORT_CALLBACK_PATH = 'supportcallback'
export const REQUEST_SALES_CALLBACK_PATH = 'salescallback'
export const REQUEST_TOURS_CALLBACK_PATH = 'tourscallback'

export const parseSelectedTravelDatesToFormText = (filters: App.OfferListFilters, intl?: IntlShape): string => {
  const anytimeText = intl?.formatMessage({
    id: 'IK/LiXm4Js',
    defaultMessage: 'Anytime',
    description: 'Default travel date text when no dates are selected',
  }) ?? 'Anytime'

  if (!filters) {
    return anytimeText
  }

  const { flexibleMonths, flexibleNights, checkIn, checkOut } = filters
  let result = anytimeText

  if (flexibleMonths) {
    const flexibleMonthsArr = flexibleMonths.split(',')
    const formattedMonthsList = flexibleMonthsArr
      .map(month => moment(month).format(SHORT_MONTH_LONG_YEAR))
    const formattedMonths = intl
      ? (intl.formatList(formattedMonthsList, { type: 'disjunction' }))
      : formattedMonthsList.join(' or ')

    if (flexibleNights) {
      // flexibleNights is a range string like '1-3', '4-7' — always plural
      result = intl?.formatMessage(
        {
          id: 'PKJhCKP7LR',
          defaultMessage: '{nights} nights, {months}',
          description: 'Travel dates text showing night range and flexible months',
        },
        { nights: flexibleNights, months: formattedMonths },
      ) ?? `${flexibleNights} nights, ${formattedMonths}`
    } else {
      result = formattedMonths
    }
  } else if (checkIn && checkOut) {
    result = intl?.formatMessage(
      {
        id: '2LQRrBMFZP',
        defaultMessage: '{checkInDate} - {checkOutDate}',
        description: 'Travel date range showing check-in to check-out dates',
      },
      {
        checkInDate: moment(checkIn).format(DMY_CASUAL_FORMAT),
        checkOutDate: moment(checkOut).format(DMY_CASUAL_FORMAT),
      },
    ) ?? `${moment(checkIn).format(DMY_CASUAL_FORMAT)} - ${moment(checkOut).format(DMY_CASUAL_FORMAT)}`
  }

  return result
}

export const parseSelectedNumberOfTravellersToFormText = (filters: App.OfferListFilters, intl: IntlShape) => {
  if (!filters) {
    return ''
  }
  const result = filters?.rooms?.reduce(
    (totals, room) => ({
      totalAdults: totals.totalAdults + (room.adults || 0),
      totalChildren: totals.totalChildren + (room.children || 0),
    }),
    { totalAdults: 0, totalChildren: 0 },
  ) || { totalAdults: 0, totalChildren: 0 }

  // changing this to "one"/"other" instead of "1"/"2"+" Adults"/" Children"
  // because linter requires "one" plural rule to be present for CLDR compliance
  const adultText = result.totalAdults > 0
    ? intl.formatMessage(
        {
          id: '/WPrqGZrAN',
          defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
          description: 'Number of adults for callback request traveller count',
        },
        { count: result.totalAdults },
      )
    : ''
  const childText = result.totalChildren > 0
    ? intl.formatMessage(
        {
          id: 'dyD5wNgjMt',
          defaultMessage: '{count, plural, one {# Child} other {# Children}}',
          description: 'Number of children for callback request traveller count',
        },
        { count: result.totalChildren },
      )
    : ''

  return [adultText, childText].filter(Boolean).join(', ')
}
